import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";

import {
    SAN_CTI_URL,
    SAN_CTI_ORIGIN,
    SAN_CTI_EVENTS,
    SAN_CTI_COMMANDS,
} from "./sanCti.constants";

import "./SanCtiWidget.css";

const SanCtiWidget = forwardRef(
    (
        {
            autoLogin = false,
            username = "",
            password = "",

            position = "bottom-right",

            onEvent,
            onReady,
            onOutgoing,
            onIncoming,
            onHold,
            onDispositionOptions,
            onSavePage,
            onManualChange,
            onBreak,
            onInit,
            onLogout,
            onConferenceJoin,
            onConferenceDialing,
            onConferenceLeave,
            onTransfer,
            onConference,

            showIframe = true,
        },
        ref
    ) => {
        const iframeRef = useRef(null);

        const [widgetOpen, setWidgetOpen] = useState(true);
        const [minimized, setMinimized] = useState(false);

        const [status, setStatus] = useState("Disconnected");
        const [extension, setExtension] = useState("");

        const [callType, setCallType] = useState("");
        const [callStatus, setCallStatus] = useState("");

        const [currentPhone, setCurrentPhone] = useState("");
        const [isHold, setIsHold] = useState(false);
        const [isMuted, setIsMuted] = useState(false);

        const [manualMode, setManualMode] = useState(false);

        const [dispositions, setDispositions] = useState([]);
        const [conferenceMembers, setConferenceMembers] = useState({});

        /**
         * ------------------------------------------------
         * Send message to SAN iframe
         * ------------------------------------------------
         */
        const sendMessage = useCallback((type, payload = {}) => {
            const iframe = iframeRef.current;

            if (!iframe?.contentWindow) {
                console.warn("SAN CTI iframe is not ready");
                return false;
            }

            iframe.contentWindow.postMessage(
                {
                    type,
                    ...payload,
                },
                SAN_CTI_ORIGIN
            );

            return true;
        }, []);

        /**
         * ------------------------------------------------
         * Parent -> SAN commands
         * ------------------------------------------------
         */

        const login = useCallback(
            (user, pass, options = {}) => {
                if (!user || !pass) {
                    console.error("SAN CTI: username/password required");
                    console.log(user, pass, options)
                    return false;
                }

                return sendMessage(SAN_CTI_COMMANDS.LOGIN,
                    {
                        user_name: user,
                        password: pass,
                        uniqueId: options.uniqueId || "",
                        verifiedFlag: options.verifiedFlag || "0",
                    }
                );
            },
            [sendMessage]
        );

        const ready = useCallback(() => {
            return sendMessage(SAN_CTI_COMMANDS.READY);
        }, [sendMessage]);

        const logout = useCallback(() => {
            console.log("logoutpresed")
            return sendMessage(SAN_CTI_COMMANDS.LOGOUT);
        }, [sendMessage]);

        const dial = useCallback(
            (number, uniqueId = "") => {
                if (!number) {
                    console.error("SAN CTI: phone number required");
                    return false;
                }

                setCurrentPhone(number);

                return sendMessage(SAN_CTI_COMMANDS.DIAL, {
                    number,
                    uniqueId,
                });
            },
            [sendMessage]
        );

        const hangup = useCallback(() => {
            return sendMessage(SAN_CTI_COMMANDS.HANGUP);
        }, [sendMessage]);

        const hold = useCallback(() => {
            setIsHold(true);
            return sendMessage(SAN_CTI_COMMANDS.HOLD);
        }, [sendMessage]);

        const unhold = useCallback(() => {
            setIsHold(false);
            return sendMessage(SAN_CTI_COMMANDS.UNHOLD);
        }, [sendMessage]);

        const toggleHold = useCallback(() => {
            return isHold ? unhold() : hold();
        }, [isHold, hold, unhold]);

        const mute = useCallback(() => {
            setIsMuted(true);
            return sendMessage(SAN_CTI_COMMANDS.MUTE);
        }, [sendMessage]);

        const unmute = useCallback(() => {
            setIsMuted(false);
            return sendMessage(SAN_CTI_COMMANDS.UNMUTE);
        }, [sendMessage]);

        const toggleMute = useCallback(() => {
            return isMuted ? unmute() : mute();
        }, [isMuted, mute, unmute]);

        const manualOn = useCallback(() => {
            setManualMode(true);
            return sendMessage(SAN_CTI_COMMANDS.MANUAL_ON);
        }, [sendMessage]);

        const manualOff = useCallback(() => {
            setManualMode(false);
            return sendMessage(SAN_CTI_COMMANDS.MANUAL_OFF);
        }, [sendMessage]);

        const toggleManual = useCallback(() => {
            return manualMode ? manualOff() : manualOn();
        }, [manualMode, manualOn, manualOff]);

        const answer = useCallback(() => {
            return sendMessage(SAN_CTI_COMMANDS.INCOMING_ACCEPT);
        }, [sendMessage]);

        /**
         * Conference
         */
        const conferenceToggle = useCallback(() => {
            return sendMessage(SAN_CTI_COMMANDS.CONFERENCE_TOGGLE);
        }, [sendMessage]);

        const conferenceAdd = useCallback(
            (phone) => {
                if (!phone) {
                    console.error("SAN CTI: conference phone required");
                    return false;
                }

                return sendMessage(SAN_CTI_COMMANDS.CONFERENCE_NUMBER, {
                    phone,
                });
            },
            [sendMessage]
        );

        const conferenceHoldToggle = useCallback(
            (phone) => {
                return sendMessage(SAN_CTI_COMMANDS.CONFERENCE_HOLD_TOGGLE, {
                    phone,
                });
            },
            [sendMessage]
        );

        /**
         * Disposition
         */
        const submitDisposition = useCallback(
            ({ disposition, remark, phone_number }) => {
                return sendMessage(SAN_CTI_COMMANDS.SUBMIT_DISPOSITION, {
                    disposition,
                    remark,
                    phone_number: phone_number || currentPhone,
                });
            },
            [currentPhone, sendMessage]
        );

        /**
         * Generic command.
         *
         * Useful if SAN later gives you another command such as
         * Transfer/Break/etc.
         */
        const sendRaw = useCallback(
            (type, payload = {}) => {
                return sendMessage(type, payload);
            },
            [sendMessage]
        );

        /**
         * ------------------------------------------------
         * Expose API to parent component
         * ------------------------------------------------
         */
        useImperativeHandle(
            ref,
            () => ({
                login,
                ready,
                logout,

                dial,
                hangup,

                hold,
                unhold,
                toggleHold,

                mute,
                unmute,
                toggleMute,

                manualOn,
                manualOff,
                toggleManual,

                answer,

                conferenceToggle,
                conferenceAdd,
                conferenceHoldToggle,

                submitDisposition,

                sendRaw,

                open: () => setWidgetOpen(true),
                close: () => setWidgetOpen(false),
                minimize: () => setMinimized(true),
                maximize: () => setMinimized(false),
                toggle: () => setWidgetOpen((value) => !value),

                getState: () => ({
                    status,
                    extension,
                    callType,
                    callStatus,
                    currentPhone,
                    isHold,
                    isMuted,
                    manualMode,
                    dispositions,
                    conferenceMembers,
                }),
            }),
            [
                login,
                ready,
                logout,
                dial,
                hangup,
                hold,
                unhold,
                toggleHold,
                mute,
                unmute,
                toggleMute,
                manualOn,
                manualOff,
                toggleManual,
                answer,
                conferenceToggle,
                conferenceAdd,
                conferenceHoldToggle,
                submitDisposition,
                sendRaw,
                status,
                extension,
                callType,
                callStatus,
                currentPhone,
                isHold,
                isMuted,
                manualMode,
                dispositions,
                conferenceMembers,
            ]
        );

        /**
         * ------------------------------------------------
         * Receive events from SAN iframe
         * ------------------------------------------------
         */
        useEffect(() => {
            const handleMessage = (event) => {
                /**
                 * SECURITY
                 *
                 * Do not accept arbitrary postMessage events.
                 */
                if (event.origin !== SAN_CTI_ORIGIN) {
                    return;
                }

                if (
                    iframeRef.current &&
                    event.source !== iframeRef.current.contentWindow
                ) {
                    return;
                }

                const message = event.data || {};
                const type = message.type;
                const data = message.payload;

                if (!type) {
                    return;
                }

                onEvent?.(message);

                switch (type) {
                    /**
                     * Agent Ready
                     */
                    case SAN_CTI_EVENTS.READY: {
                        setStatus("Idle");

                        if (data?.exten) {
                            setExtension(data.exten);
                        }

                        onReady?.(data);
                        break;
                    }

                    /**
                     * Outgoing call
                     */
                    case SAN_CTI_EVENTS.OUTGOING: {
                        setCallType(data?.call_type || "Outgoing");
                        setCallStatus(data?.exten_status || "");

                        if (data?.caller_id) {
                            setCurrentPhone(data.caller_id);
                        }

                        if (data?.exten) {
                            setExtension(data.exten);
                        }

                        if (data?.exten_status === "Answer") {
                            setStatus("In Call");
                        }

                        if (data?.exten_status === "Hangup") {
                            setStatus("Hangup");
                            setIsHold(false);
                            setIsMuted(false);
                        }

                        onOutgoing?.(data);
                        break;
                    }

                    /**
                     * Incoming call
                     */
                    case SAN_CTI_EVENTS.INCOMING: {
                        setCallType(data?.call_type || "Incoming");
                        setCallStatus(data?.exten_status || "");

                        if (data?.caller_id) {
                            setCurrentPhone(data.caller_id);
                        }

                        if (data?.exten) {
                            setExtension(data.exten);
                        }

                        if (data?.exten_status === "Ringing") {
                            setStatus("Ringing");
                        }

                        if (data?.exten_status === "Answer") {
                            setStatus("In Call");
                        }

                        if (data?.exten_status === "Hangup") {
                            setStatus("Hangup");
                        }

                        onIncoming?.(data);
                        break;
                    }

                    /**
                     * Hold
                     */
                    case SAN_CTI_EVENTS.HOLD: {
                        const holdState =
                            String(data?.set ?? data?.hold) === "1";

                        setIsHold(holdState);
                        setStatus(holdState ? "Hold" : "In Call");

                        onHold?.(data);
                        break;
                    }

                    /**
                     * Save/disposition completed
                     */
                    case SAN_CTI_EVENTS.SAVE_PAGE: {
                        setStatus("Idle");
                        setCallStatus("");

                        setIsHold(false);
                        setIsMuted(false);

                        onSavePage?.(data);
                        break;
                    }

                    /**
                     * Manual mode
                     */
                    case SAN_CTI_EVENTS.MANUAL: {
                        const isManual =
                            String(data?.state) === "10";

                        setManualMode(isManual);
                        setStatus(
                            isManual
                                ? "Manual Dial On"
                                : "Manual Dial Off"
                        );

                        onManualChange?.(data);
                        break;
                    }

                    /**
                     * Break
                     */
                    case SAN_CTI_EVENTS.BREAK: {
                        if (data?.state === 4) {
                            setStatus(
                                `On Break${data?.data?.break_name
                                    ? ` - ${data.data.break_name}`
                                    : ""
                                }`
                            );
                        }

                        onBreak?.(data);
                        break;
                    }

                    /**
                     * Initial SAN state
                     */
                    case SAN_CTI_EVENTS.INIT: {
                        if (data?.login_extension_no) {
                            setExtension(data.login_extension_no);
                        }

                        onInit?.(data);
                        break;
                    }

                    /**
                     * Logout
                     */
                    case SAN_CTI_EVENTS.LOGOUT: {
                        setStatus("Logout");
                        setCallStatus("");

                        onLogout?.(data);
                        break;
                    }

                    /**
                     * Disposition list from SAN
                     */
                    case SAN_CTI_EVENTS.SEND_DISPOSITION: {
                        const list = Array.isArray(data)
                            ? data
                            : [];

                        setDispositions(list);
                        onDispositionOptions?.(list);
                        break;
                    }

                    /**
                     * Conference joined
                     */
                    case SAN_CTI_EVENTS.CONFERENCE_JOIN: {
                        const members =
                            data?.conf_memeber || {};

                        setConferenceMembers(members);

                        onConferenceJoin?.({
                            members,
                            data: data?.data,
                        });

                        break;
                    }

                    /**
                     * Conference dialing
                     */
                    case SAN_CTI_EVENTS.CONFERENCE_DIALING: {
                        const members =
                            data?.conference_dialing_members || {};

                        onConferenceDialing?.({
                            members,
                            data: data?.data,
                        });

                        break;
                    }

                    /**
                     * Conference leave
                     */
                    case SAN_CTI_EVENTS.CONFERENCE_LEAVE: {
                        const members =
                            data?.conf_memeber || {};

                        setConferenceMembers(members);

                        onConferenceLeave?.({
                            members,
                            data: data?.data,
                        });

                        break;
                    }

                    /**
                     * Transfer event
                     *
                     * The integration PDF documents this event.
                     */
                    case SAN_CTI_EVENTS.TRANSFER: {
                        onTransfer?.(data);
                        break;
                    }

                    /**
                     * Conference event
                     */
                    case SAN_CTI_EVENTS.CONFERENCE: {
                        onConference?.(data);
                        break;
                    }

                    default:
                        break;
                }
            };

            window.addEventListener("message", handleMessage);

            return () => {
                window.removeEventListener("message", handleMessage);
            };
        }, [
            onEvent,
            onReady,
            onOutgoing,
            onIncoming,
            onHold,
            onDispositionOptions,
            onSavePage,
            onManualChange,
            onBreak,
            onInit,
            onLogout,
            onConferenceJoin,
            onConferenceDialing,
            onConferenceLeave,
            onTransfer,
            onConference,
        ]);

        /**
         * Optional auto login.
         */
        useEffect(() => {
            if (!autoLogin) {
                return;
            }

            if (!username || !password) {
                return;
            }

            const timer = setTimeout(() => {
                login(username, password);
            }, 1000);

            return () => clearTimeout(timer);
        }, [autoLogin, username, password, login]);

        if (!showIframe || !widgetOpen) {
            return null;
        }

        return (
            <div
                className={`san-cti-widget ${position} ${minimized ? "san-cti-minimized" : ""
                    }`}
                style={{ display: "none" }}
            >
                <div className="san-cti-header">
                    <div className="san-cti-header-left">
                        <span className="san-cti-status-dot" />

                        <div>
                            <div className="san-cti-title">
                                Dialer
                            </div>

                            <div className="san-cti-subtitle">
                                {status}
                                {extension
                                    ? ` • Ext. ${extension}`
                                    : ""}
                            </div>
                        </div>
                    </div>

                    <div className="san-cti-header-actions">
                        <button
                            type="button"
                            onClick={() =>
                                setMinimized((value) => !value)
                            }
                        >
                            {minimized ? "+" : "−"}
                        </button>

                        <button
                            type="button"
                            onClick={() => setWidgetOpen(false)}
                        >
                            ×
                        </button>
                    </div>
                </div>

                {!minimized && (
                    <div className="san-cti-iframe-wrapper">
                        <iframe
                            ref={iframeRef}
                            title="SAN CTI"
                            src={SAN_CTI_URL}
                            allow="microphone; camera"
                            frameBorder="0"
                        />
                    </div>
                )}
            </div>
        );
    }
);

SanCtiWidget.displayName = "SanCtiWidget";

export default SanCtiWidget;